import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useFetch, Card, Loader, PageHeader, Button, Field } from "../ui";
import { CheckCircle2, Circle, Rocket, ShieldCheck } from "lucide-react";
import { PostLaunchTab } from "./PostLaunchTab";

const TABS = ["Onboarding", "Strategy", "Approvals", "QA", "Launch", "Post-Launch"];

function OnboardingTab({ cid }) {
  const { data, reload } = useFetch(`/admin/delivery/onboarding/${cid}`, [cid]);
  const { data: tpls } = useFetch("/admin/delivery/templates", []);
  const [tpl, setTpl] = useState("cinematic-website");
  if (!data) return <Loader />;
  const toggle = async (item, done) => {
    try { await api.post(`/admin/delivery/onboarding/${cid}/check`, { item, done }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5" data-testid="dw-onboarding-checklist">
        <p className="mb-3 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Onboarding Checklist {data.complete && <span className="text-[#C6FF00]">— COMPLETE</span>}</p>
        {Object.entries(data.items).map(([k, done]) => (
          <div key={k} className="mt-2 flex items-center justify-between rounded-xl bg-[#14161b] px-4 py-3">
            <span className="flex items-center gap-3 text-sm font-bold text-white">
              {done ? <CheckCircle2 className="h-5 w-5 text-[#C6FF00]" /> : <Circle className="h-5 w-5 text-[#3a3f47]" />} {k}
            </span>
            {(k === "CONTRACT" || k === "KICKOFF") ? (
              <Button variant="ghost" data-testid={`dw-check-${k}`} onClick={() => toggle(k, !done)}>{done ? "Undo" : "Mark Done"}</Button>
            ) : <span className="text-xs text-[#6B7280]">auto-tracked</span>}
          </div>
        ))}
      </Card>
      <Card className="p-5" data-testid="dw-assets">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Asset Collection</p>
        <div className="flex flex-wrap gap-2">
          {data.assets.required.map((r) => (
            <span key={r} className={`rounded-full px-3 py-1.5 text-xs font-extrabold ${data.assets.missing.includes(r) ? "bg-amber-500/15 text-amber-400" : "bg-[#C6FF00]/10 text-[#C6FF00]"}`}>
              {r}: {data.assets.missing.includes(r) ? "MISSING" : "RECEIVED"}
            </span>
          ))}
        </div>
        <p className="mt-3 text-xs text-[#6B7280]">Missing assets are auto-chased every 3 days (max 3 reminders), then escalated to you.</p>
      </Card>
      <Card className="p-5" data-testid="dw-questionnaire">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Questionnaire {data.questionnaire?.completed_at ? "— COMPLETED" : "— PENDING"}</p>
        {data.questionnaire?.answers ? data.questions.map((q) => (
          data.questionnaire.answers[q.key] ? <p key={q.key} className="mt-2 text-sm"><span className="font-bold text-[#8A939B]">{q.label}</span><br /><span className="text-[#c8cdd3]">{data.questionnaire.answers[q.key]}</span></p> : null
        )) : <p className="text-sm text-[#6B7280]">Client hasn't started the questionnaire yet — it's requested automatically in their Action Center.</p>}
      </Card>
      <Card className="p-5" data-testid="dw-template">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Project Template</p>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="dw-template-select" value={tpl} onChange={(e) => setTpl(e.target.value)}
            className="rounded-xl border border-[#23262c] bg-[#14161b] px-3 py-2.5 text-sm text-white">
            {(tpls?.templates || []).map((t) => <option key={t.key} value={t.key}>{t.name} ({t.milestones.length} milestones)</option>)}
          </select>
          <Button data-testid="dw-apply-template" onClick={async () => { try { const r = await api.post(`/admin/delivery/apply-template/${cid}/${tpl}`); toast.success(`${r.data.tasks_created} milestone tasks created`); } catch (e) { toast.error(apiError(e)); } }}>Apply Template</Button>
        </div>
      </Card>
    </div>
  );
}

function StrategyTab({ cid }) {
  const { data, reload } = useFetch(`/admin/delivery/strategy/${cid}`, [cid]);
  const [sections, setSections] = useState(null);
  if (!data) return <Loader />;
  const s = sections || data.strategy.sections;
  const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <Card className="p-5" data-testid="dw-strategy">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-wider text-[#8A939B]">Strategy Workspace {data.strategy.approved && <span className="text-[#C6FF00]">— FOUNDER APPROVED</span>}</p>
        {data.strategy.prefill_note && <span className="rounded-full bg-[#14161b] px-3 py-1 text-xs text-[#8A939B]">{data.strategy.prefill_note}</span>}
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        {data.section_keys.map((k) => (
          <label key={k} className="block">
            <span className="mb-1 block text-[10px] font-extrabold uppercase tracking-wider text-[#C6FF00]">{label(k)}</span>
            <textarea data-testid={`dw-strategy-${k}`} rows={3} value={s[k] || ""} onChange={(e) => setSections({ ...s, [k]: e.target.value })}
              className="w-full rounded-xl border border-[#23262c] bg-[#14161b] px-3 py-2 text-sm text-white outline-none focus:border-[#C6FF00]" />
          </label>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <Button variant="ghost" data-testid="dw-strategy-save" onClick={async () => { try { await api.put(`/admin/delivery/strategy/${cid}`, { sections: s }); toast.success("Strategy saved"); reload(); } catch (e) { toast.error(apiError(e)); } }}>Save Draft</Button>
        <Button data-testid="dw-strategy-approve" onClick={async () => { if (!window.confirm("Approve this strategy? If the client is in STRATEGY stage they auto-advance to BUILD.")) return; try { await api.post(`/admin/delivery/strategy/${cid}/approve`); toast.success("Strategy approved — BUILD unlocked"); reload(); } catch (e) { toast.error(apiError(e)); } }}><ShieldCheck className="mr-1 h-4 w-4" /> Founder Approve</Button>
      </div>
    </Card>
  );
}

function ApprovalsTab({ cid }) {
  const { data, reload } = useFetch(`/admin/delivery/approvals/${cid}`, [cid]);
  const [form, setForm] = useState({ type: "DESIGN DIRECTION", title: "", description: "", link: "", version: "v1" });
  if (!data) return <Loader />;
  const STATUS = { REQUESTED: "text-amber-400", APPROVED: "text-[#C6FF00]", CHANGES_REQUESTED: "text-red-400", QUESTION: "text-sky-400" };
  return (
    <div className="space-y-4">
      <Card className="p-5" data-testid="dw-approval-form">
        <p className="mb-3 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Request Client Approval</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <select data-testid="dw-approval-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-xl border border-[#23262c] bg-[#14161b] px-3 py-2.5 text-sm text-white">
            {["STRATEGY", "SITEMAP", "DESIGN DIRECTION", "COPY", "WEBSITE", "LAUNCH"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <Field placeholder="Title (e.g. Homepage design direction)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="dw-approval-title" />
          <Field placeholder="Link to review (optional)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="dw-approval-link" />
          <Field placeholder="Version (v1)" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="dw-approval-version" />
        </div>
        <Button className="mt-3" data-testid="dw-approval-send" onClick={async () => { if (!form.title) return toast.error("Title required"); try { await api.post(`/admin/delivery/approvals/${cid}`, form); toast.success("Approval requested — client notified"); setForm({ ...form, title: "" }); reload(); } catch (e) { toast.error(apiError(e)); } }}>Send Approval Request</Button>
      </Card>
      {data.approvals.map((a) => (
        <Card key={a.id} className="p-4" data-testid={`dw-approval-${a.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-bold text-white">{a.type} · {a.title} <span className="text-xs text-[#6B7280]">({a.version})</span></p>
            <span className={`text-xs font-extrabold ${STATUS[a.status] || ""}`}>{a.status}</span>
          </div>
          {(a.comments || []).map((c, i) => <p key={i} className="mt-2 rounded-xl bg-[#14161b] p-2.5 text-sm text-[#c8cdd3]"><span className="font-bold text-white">{c.by}</span> · {c.action} · {new Date(c.at).toLocaleString()}<br />{c.comment}</p>)}
        </Card>
      ))}
      {data.approvals.length === 0 && <p className="text-sm text-[#6B7280]">No approvals requested yet.</p>}
    </div>
  );
}

function QaTab({ cid }) {
  const { data, reload } = useFetch(`/admin/delivery/qa/${cid}`, [cid]);
  if (!data) return <Loader />;
  return (
    <Card className="p-5" data-testid="dw-qa">
      <p className="mb-3 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Pre-Launch QA — {data.done}/{data.total} {data.complete && <span className="text-[#C6FF00]">COMPLETE</span>}</p>
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {data.qa.items.map((i) => (
          <button key={i.key} data-testid={`dw-qa-${i.key}`} onClick={async () => { await api.post(`/admin/delivery/qa/${cid}/toggle`, { key: i.key, done: !i.done }); reload(); }}
            className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors ${i.done ? "bg-[#C6FF00]/10 text-[#C6FF00]" : "bg-[#14161b] text-[#8A939B] hover:text-white"}`}>
            {i.done ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <Circle className="h-4 w-4 shrink-0" />} {i.label}
          </button>
        ))}
      </div>
    </Card>
  );
}

function LaunchTab({ cid }) {
  const { data, reload } = useFetch(`/admin/delivery/launch-readiness/${cid}`, [cid]);
  if (!data) return <Loader />;
  return (
    <Card className="p-6 text-center" data-testid="dw-launch">
      <p className="text-6xl font-extrabold text-white">{data.score}<span className="text-2xl text-[#C6FF00]">%</span></p>
      <p className="mt-1 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Launch Readiness</p>
      <div className="mx-auto mt-5 max-w-md space-y-1.5 text-left">
        {data.checks.map((c) => (
          <p key={c.label} className="flex items-center gap-2.5 text-sm">{c.done ? <CheckCircle2 className="h-4 w-4 text-[#C6FF00]" /> : <Circle className="h-4 w-4 text-[#3a3f47]" />} <span className={c.done ? "text-white" : "text-[#8A939B]"}>{c.label}</span></p>
        ))}
      </div>
      <Button className="mt-6" data-testid="dw-launch-btn" onClick={async () => {
        if (!window.confirm("FOUNDER APPROVAL — launch this project? This marks it LAUNCHED, notifies the client, and schedules the day-7 check + 30-day review.")) return;
        try { await api.post(`/admin/delivery/launch/${cid}`, { confirm: true }); toast.success("🚀 Launched — post-launch workflow scheduled"); reload(); } catch (e) { toast.error(apiError(e)); }
      }}><Rocket className="mr-1 h-4 w-4" /> Approve & Launch</Button>
      <p className="mt-3 text-xs text-[#6B7280]">RED-level action — requires your explicit confirmation. QA must be 100%.</p>
    </Card>
  );
}

export default function DeliveryWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Onboarding");
  const { data } = useFetch(`/admin/delivery/record/${id}`, [id]);
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Delivery OS" title={data?.client?.company_name || "Delivery Workspace"}
        subtitle="Onboarding → Strategy → Approvals → QA → Launch"
        actions={<Button variant="ghost" onClick={() => navigate(`/admin/record/${id}`)} data-testid="dw-back-record">Universal Record</Button>} />
      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button key={t} data-testid={`dw-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`rounded-full px-4 py-2 text-sm font-extrabold ${tab === t ? "bg-[#C6FF00] text-[#0A0C0F]" : "bg-[#14161b] text-[#8A939B] hover:text-white"}`}>{t}</button>
        ))}
      </div>
      {tab === "Onboarding" && <OnboardingTab cid={id} />}
      {tab === "Strategy" && <StrategyTab cid={id} />}
      {tab === "Approvals" && <ApprovalsTab cid={id} />}
      {tab === "QA" && <QaTab cid={id} />}
      {tab === "Launch" && <LaunchTab cid={id} />}
      {tab === "Post-Launch" && <PostLaunchTab cid={id} />}
    </div>
  );
}
